// Resolves a thrown error to the copy the user should see on 약관 관리.
//
// SHCT-702 — this used to end in `defaultValue: err.message`, so a code with no key here published the
// server's own text. A 500 answers with `INTERNAL_ERROR`, which the old `UNKNOWN`/`NETWORK` guard did
// not list, and PRD 14 §8.2's "새 버전 등록에 실패했습니다…" was replaced on screen by a raw exception
// string. The shared resolver inverts the rule: show a message only when this app has copy for it.

import type { TFunction } from 'i18next';

import { resolveApiErrorMessage } from '@/shared/utils/api-error-message';

import { TermsApiError } from '../types/terms.types';

export function getTermsErrorMessage(
  err: unknown,
  t: TFunction,
  fallbackKey: string,
): string {
  const code = err instanceof TermsApiError ? err.code : null;
  return resolveApiErrorMessage(t, code, fallbackKey);
}

/**
 * The form field each PRD 14 §8.1 validation code belongs under.
 *
 * §8.1 places every one of these beneath a specific field — 버전 번호 중복 under 버전, and so on. The
 * server, though, is only sometimes able to say so: it returns the code always but the `fields` map
 * only sometimes. When it does not, the message used to fall through to the generic system-error path
 * and surface as an operation-level failure, so a duplicate version number — a thing the operator can
 * see and fix in the field right in front of them — read as "saving failed", with nothing marked.
 *
 * Only codes §8.1 actually places under a field appear here. A business code that the PRD does not
 * position (VERSION_NOT_PUBLISHABLE, INSUFFICIENT_PERMISSION, …) is deliberately absent: it has no
 * field to sit under, and inventing one would point the operator at the wrong input.
 */
const FIELD_BY_ERROR_CODE: Readonly<Record<string, string>> = {
  VERSION_REQUIRED: 'version',
  VERSION_FORMAT_INVALID: 'version',
  VERSION_DUPLICATE: 'version',
  EFFECTIVE_DATE_REQUIRED: 'effectiveDate',
  EFFECTIVE_DATE_PAST: 'effectiveDate',
  CONTENT_REQUIRED: 'content',
};

/**
 * The inline field errors a failure should show (PRD 14 §8.1), or null when it is not a §8.1 failure
 * and therefore belongs in the §8.2 popup instead.
 *
 * **The server says WHICH field; this app says WHAT it reads.** The server's `fields` map is the
 * authority on placement — it can name several fields at once and it knows which ones it rejected —
 * but its values are the backend's own copy, which is not the PRD's. SHCT-702: a duplicate version
 * number arrives as `fields: { version: "이미 존재하는 버전입니다." }` while PRD 14 §8.1 words it
 * "이미 존재하는 버전 번호입니다.", so passing the map straight through put the wrong sentence under
 * the field. The keys are kept, the text is re-resolved from this app's catalogue, and the server's
 * own line survives only where there is no copy for the code.
 */
export function getTermsFieldErrors(
  err: unknown,
  t: TFunction,
): Record<string, string> | null {
  if (!(err instanceof TermsApiError)) return null;

  const localized = t(`errors.${err.code}`, { defaultValue: '' });

  if (err.fields && Object.keys(err.fields).length > 0) {
    // Only the field this CODE is about gets re-worded. The map may name others for reasons the code
    // does not describe — a second field the server rejected in the same request — and this app has no
    // copy for those, so their server text is the only thing available and is left alone.
    const ownedByCode = FIELD_BY_ERROR_CODE[err.code];
    return Object.fromEntries(
      Object.entries(err.fields).map(([field, serverText]) => [
        field,
        field === ownedByCode && localized ? localized : serverText,
      ]),
    );
  }

  const field = FIELD_BY_ERROR_CODE[err.code];
  if (!field) return null;
  return { [field]: localized || err.message };
}
